/**
 * @fileoverview Accept-invite page to resolve token and join organization.
 * @module features/auth/pages/AcceptInvitePage
 */

import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { authService } from '@/lib/authService'
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui'
import { type OrgId, OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'
import { z } from 'zod'
import { acceptInvite } from '@/features/orgs/api/invitesService'
import { switchOrg } from '@/features/preferences/api/preferencesService'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'

/** Storage key for pending invite token to help users resume flow after auth. */
const PENDING_INVITE_TOKEN_KEY = 'pendingInviteToken'
/** Storage key for the org ID associated with the pending invite. */
const PENDING_INVITE_ORG_KEY = 'pendingInviteOrgId'

/**
 * Parse query string into a URLSearchParams object.
 */
function useQuery(): URLSearchParams {
  const { search } = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

/**
 * AcceptInvitePage handles joining an organization via an opaque invite token.
 * - If not authenticated, prompts user to sign in/sign up and stores token for later.
 * - If authenticated, resolves the token to an org id (if not provided), calls accept endpoint,
 *   refreshes profile and switches active org, then navigates home.
 *
 * @component
 */
export function AcceptInvitePage(): ReactElement {
  const { t } = useTranslation('features-auth')
  const navigate = useNavigate()
  const query = useQuery()
  const { refreshFromProfile } = useActiveOrg()
  const [error, setError] = useState<string>('')
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const tokenFromQuery = query.get('token') || ''
  const orgIdFromQuery = query.get('orgId') || '' // optional escape hatch if provided
  const { orgId: orgIdFromPath } = useParams<{ orgId?: string }>()

  const ensureToken = useCallback((): string | null => {
    const token = tokenFromQuery.trim()
    if (token) return token
    try {
      const saved = localStorage.getItem(PENDING_INVITE_TOKEN_KEY)
      return saved && saved.length > 0 ? saved : null
    } catch {
      return null
    }
  }, [tokenFromQuery])

  const handleAccept = useCallback(async () => {
    setError('')
    setIsProcessing(true)
    try {
      const token = ensureToken()
      if (!token) throw new Error(t('acceptInvite.errors.missingToken'))
      // Validate token format eagerly (uuid)
      z.string().uuid(t('acceptInvite.errors.invalidToken')).parse(token)

      // Resolve orgId: prefer query value if present; otherwise call backend resolve endpoint
      let orgId: OrgId | null = null
      const candidate = orgIdFromQuery || orgIdFromPath || ''
      if (!candidate) throw new Error(t('acceptInvite.errors.missingOrgId'))
      orgId = OrgIdSchema.parse(candidate)

      await acceptInvite(orgId, token)
      // Switch active org and hydrate context with the returned profile in one step
      const profile = await switchOrg({ organizationId: orgId })
      refreshFromProfile({
        organizations: profile.organizations,
        defaultOrgId: profile.defaultOrgId ?? null,
        ...(profile.preferredLanguage ? { preferredLanguage: profile.preferredLanguage } : {}),
      })

      // Clean up pending token and navigate home
      try {
        localStorage.removeItem(PENDING_INVITE_TOKEN_KEY)
        localStorage.removeItem(PENDING_INVITE_ORG_KEY)
      } catch {}
      navigate('/dashboard')
    } catch (e) {
      const anyErr = e as { message?: string }
      setError(anyErr?.message || t('acceptInvite.errors.fallback'))
    } finally {
      setIsProcessing(false)
    }
  }, [ensureToken, orgIdFromQuery, orgIdFromPath, navigate, refreshFromProfile, t])

  useEffect(() => {
    // If not authenticated, prompt to sign in and persist token for later
    if (!authService.isAuthenticated()) {
      try {
        const tok = ensureToken()
        if (tok) localStorage.setItem(PENDING_INVITE_TOKEN_KEY, tok)
        const oid = orgIdFromQuery || orgIdFromPath || ''
        if (oid) localStorage.setItem(PENDING_INVITE_ORG_KEY, oid)
      } catch {}
      return
    }
    // If authenticated, attempt acceptance immediately
    void handleAccept()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authService.isAuthenticated()])

  const authed = authService.isAuthenticated()

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>{t('acceptInvite.title')}</CardTitle>
            <CardDescription className="text-muted-foreground">
              {authed ? t('acceptInvite.joiningOrg') : t('acceptInvite.signInPrompt')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="mb-4 rounded-lg bg-destructive p-3 text-sm text-white">{error}</div>
            )}
            {authed ? (
              <div className="grid gap-3">
                <Button
                  type="button"
                  className="w-full"
                  disabled={isProcessing}
                  onClick={() => handleAccept()}
                >
                  {isProcessing ? t('acceptInvite.processing') : t('acceptInvite.retryAcceptance')}
                </Button>
              </div>
            ) : (
              <div className="grid gap-3">
                <Button type="button" className="w-full" onClick={() => navigate('/')}>
                  {t('acceptInvite.goToSignIn')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => navigate('/signup')}
                >
                  {t('acceptInvite.createAccount')}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
